<template>
  <div id="app">

    <!-- ヘッダー -->
    <header class="page-header">
      <p class="page-description">
        <a href="https://k-ho-ko.com/publicationconditions-managementpolicy/" target="_blank">
          掲載条件・運営方針
        </a>
        をご確認の上、企業情報を入力してください。
      </p>
    </header>

    <!-- フォーム本体 -->
    <div class="form-container">

      <!-- 企業情報 -->
      <section class="form-section">
        <h2 class="section-title">企業情報</h2>

        <!-- タイトル -->
        <div class="form-group">
          <label class="form-label">
            タイトル <span class="badge-required">必須</span>
          </label>
          <input
            type="text"
            v-model="form.title"
            :class="['form-input', { 'is-error': validation_errors.title }]"
            placeholder="例）◯◯株式会社 など"
          />
          <p v-if="validation_errors.title" class="form-error">
            タイトルを入力してください。
          </p>
        </div>

        <!-- アイキャッチ画像 -->
        <div class="form-group">
          <label class="form-label">
            アイキャッチ画像 <span class="badge-required">必須</span>
          </label>
          <input type="file" class="form-input" @change="handleFileUpload" />
          <p class="form-help">推奨サイズ：1200px以下。JPG / PNG 形式。</p>
          <p v-if="validation_errors.thumbnail" class="form-error">
            アイキャッチ画像を選択してください。
          </p>
        </div>

        <!-- 企業名・電話番号・所在地 -->
        <div
          v-for="(item, index) in form.companyInfo"
          :key="index"
          class="form-group"
        >
          <label class="form-label">
            {{ item.name }}
            <span v-if="item.required" class="badge-required">必須</span>
          </label>

          <input
            type="text"
            v-model="item.content"
            @input="item.name === '電話番号' ? restrictPhone(item) : null"
            :class="[
              'form-input',
              {
                'is-error':
                  validation_errors.companyInfo &&
                  validation_errors.companyInfo[index]
              }
            ]"
            :placeholder="item.placeholder || ''"
          />

          <p
            v-if="
              validation_errors.companyInfo &&
              validation_errors.companyInfo[index]
            "
            class="form-error"
          >
            {{ validation_errors.companyInfo[index] }}
          </p>
        </div>

        <!-- 任意項目追加 -->
        <div class="form-group form-group-inline">
          <div class="flex-grow">
            <label class="form-label">項目の追加</label>
            <input
              type="text"
              v-model="form.companyInfoName"
              class="form-input"
              placeholder="例）FAX、URLなど"
            />
          </div>
          <button type="button" class="btn btn-secondary add-btn" @click="addCompanyInfo">
            追加
          </button>
        </div>
      </section>

      <!-- 本文エリア -->
      <section class="form-section">
        <h2 class="section-title">紹介文・詳細情報</h2>
        <p class="section-description">
          サービス内容や事業の強みなど、自由にご記入ください。
        </p>

        <!-- タブ切り替え -->
        <div class="tab-buttons">
          <button
            type="button"
            :class="['tab-button', { active: selectTab === 'editor' }]"
            @click="selectTab = 'editor'"
          >
            エディター
          </button>
          <button
            type="button"
            :class="['tab-button', { active: selectTab === 'preview' }]"
            @click="selectTab = 'preview'"
          >
            プレビュー
          </button>
        </div>

        <!-- エディタ -->
        <div v-if="selectTab === 'editor'" class="editor-wrapper">
          <vue-editor
            v-model="form.content"
            :editorToolbar="customToolbar"
          />
        </div>

        <!-- プレビュー -->
        <div v-if="selectTab === 'preview'" class="preview-box">
          <h3 class="preview-title">プレビュー</h3>

          <div v-if="form.thumbnail_url" style="text-align:center; margin: 20px 0;">
            <img :src="form.thumbnail_url" style="max-width:100%; height:auto;" />
          </div>

          <div class="preview-content" v-html="form.content"></div>
          <div class="preview-table" v-html="companyInfoTable"></div>
        </div>
      </section>

      <!-- カテゴリー（単一選択） -->
      <section class="form-section">
        <h2 class="section-title">カテゴリー</h2>
        <p class="section-description">1つ選択してください。</p>

        <div class="category-list">
          <label
            v-for="cat in filteredCategories"
            :key="cat.id"
            class="category-item"
          >
            <input
              type="radio"
              name="category"
              :value="cat.id"
              v-model="form.category"
            />
            <span>{{ cat.name }}</span>
          </label>
        </div>

        <p v-if="validation_errors.category" class="form-error">
          カテゴリを選択してください。
        </p>
      </section>

      <!-- 掲載条件 -->
      <section class="form-section">
        <h2 class="section-title">掲載条件への同意</h2>
        <div class="policy-box">
          <label class="policy-label">
            <input type="checkbox" v-model="policy_check" />
            <span>掲載条件・運営方針に同意する</span>
          </label>
        </div>
        <p v-if="validation_errors.policy" class="form-error">
          掲載条件・運営方針への同意が必要です。
        </p>
      </section>

      <!-- 送信 -->
      <section class="form-section form-section-submit">
        <p v-if="error_message" class="form-error" style="margin-bottom: 10px;">
          {{ error_message }}
        </p>

        <p v-if="send_success" class="form-success" style="margin-bottom: 10px;">
          送信が完了しました。ご依頼、ありがとうございました。
        </p>

        <button
          class="btn btn-primary submit-btn"
          :disabled="is_sending"
          @click="submitPost"
        >
          {{ is_sending ? "送信しています…" : "この内容で送信する" }}
        </button>

        <p class="submit-note">
          ※送信完了まで、数秒かかる場合があります。
        </p>
      </section>

    </div>
  </div>
</template>






<script>
import { VueEditor } from "vue2-editor";
import axios from "axios";
import Quill from "quill";

export default {
  name: "App",
  components: { VueEditor },

  data() {
    return {
      send_success: false,
      policy_check: false,
      selectTab: "editor",
      categories: [],
      is_sending: false,
      error_message: "",

      validation_errors: {
        title: false,
        thumbnail: false,
        companyInfo: [],
        policy: false,
        category: false
      },

      customToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link", "image"]
      ],

      form: {
        thumbnail_file: null,
        title: "",
        content: "",
        status: "draft",
        category: "",   // ★ 単一カテゴリ
        companyInfoName: "",
        companyInfo: [
          { name: "企業名", content: "", required: true, placeholder: "例）◯◯株式会社" },
          { name: "電話番号", content: "", required: true, placeholder: "例）03-0000-0000" },
          { name: "所在地", content: "", required: true, placeholder: "例）東京都◯◯区◯◯ 1-2-3" }
        ]
      }
    };
  },

  computed: {
    /* ★ 企業情報カテゴリを除外したカテゴリ一覧 */
    filteredCategories() {
      return this.categories.filter(cat => cat.name !== "企業情報");
    },

    companyInfoTable() {
      let html = `
<!-- wp:table -->
<figure class="wp-block-table">
<table>
<tbody>
`;

      this.form.companyInfo.forEach((item) => {
        if (!item.content) return;

        html += `
<tr>
  <td>${this.escapeHtml(item.name)}</td>
  <td>${this.sanitize(item.content)}</td>
</tr>
`;
      });

      html += `
</tbody>
</table>
</figure>
<!-- /wp:table -->
`;

      return html;
    }
  },

  methods: {
    convertToBlocks(html) {
      const div = document.createElement("div");
      div.innerHTML = html;

      let output = "";
      const children = Array.from(div.childNodes);

      children.forEach(node => {
        if (node.tagName === "IMG") {
          const url = node.getAttribute("src");
          if (url) {
            output += `
<!-- wp:image {"align":"center"} -->
<figure class="wp-block-image aligncenter">
  <img src="${url}" alt="" />
</figure>
<!-- /wp:image -->

`;
          }
          return;
        }

        if (node.tagName === "UL") {
          const items = Array.from(node.querySelectorAll("li"))
            .map(li => `<li>${li.innerHTML}</li>`)
            .join("");

          if (items.trim() !== "") {
            output += `
<!-- wp:list {"ordered":false} -->
<ul>${items}</ul>
<!-- /wp:list -->

`;
          }
          return;
        }

        if (node.tagName === "OL") {
          const items = Array.from(node.querySelectorAll("li"))
            .map(li => `<li>${li.innerHTML}</li>`)
            .join("");

          if (items.trim() !== "") {
            output += `
<!-- wp:list {"ordered":true} -->
<ol>${items}</ol>
<!-- /wp:list -->

`;
          }
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
          const text = node.innerHTML || node.textContent;
          if (text && text.trim() !== "") {
            output += `
<!-- wp:paragraph -->
<p>${text}</p>
<!-- /wp:paragraph -->

`;
          }
        }
      });

      return output.trim();
    },

    escapeHtml(text) {
      if (typeof text !== "string") return "";
      return text.replace(/[&<>"']/g, (m) => {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;"
        }[m];
      });
    },

    restrictPhone(item) {
      let v = item.content;
      v = v
        .replace(/[！-～]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
        .replace(/\u3000/g, " ");
      v = v.replace(/[^0-9-]/g, "");
      v = v.replace(/－/g, "-");
      item.content = v;
    },

    sanitize(html) {
      if (typeof html !== "string") return "";

      const div = document.createElement("div");
      div.innerHTML = html;

      const allowed = ["STRONG", "EM", "S", "BR", "UL", "OL", "LI", "IMG"];

      const walker = document.createTreeWalker(div, NodeFilter.SHOW_ELEMENT, null);

      let node;
      const toRemove = [];

      while ((node = walker.nextNode())) {
        if (!allowed.includes(node.tagName)) {
          toRemove.push(node);
        }
      }

      toRemove.forEach(node => {
        const text = document.createTextNode(node.textContent);
        node.replaceWith(text);
      });

      return div.innerHTML;
    },

    validateForm() {
      let hasError = false;

      this.validation_errors = {
        title: false,
        thumbnail: false,
        companyInfo: [],
        policy: false,
        category: false
      };
      this.error_message = "";

      if (!this.form.title) {
        this.validation_errors.title = true;
        hasError = true;
      }

      if (!this.form.thumbnail_file) {
        this.validation_errors.thumbnail = true;
        hasError = true;
      }

      this.form.companyInfo.forEach((item, index) => {
        if (item.required && !item.content) {
          this.$set(this.validation_errors.companyInfo, index, `${item.name}を入力してください。`);
          hasError = true;
        }
      });

      if (!this.policy_check) {
        this.validation_errors.policy = true;
        hasError = true;
      }

      // ★ 単一カテゴリ必須
      if (!this.form.category) {
        this.validation_errors.category = true;
        this.error_message = "カテゴリを選択してください。";
        hasError = true;
      }

      if (hasError && !this.error_message) {
        this.error_message =
          "入力内容に不備があります。各項目のエラーメッセージをご確認のうえ、修正をお願いいたします。";
      }

      return !hasError;
    },

    addCompanyInfo() {
      if (!this.form.companyInfoName) return;
      this.form.companyInfo.push({
        name: this.form.companyInfoName,
        content: "",
        required: false,
        placeholder: ""
      });
      this.form.companyInfoName = "";
    },

    handleFileUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.form.thumbnail_file = file;
      this.validation_errors.thumbnail = false;
    },

    resetForm() {
      this.policy_check = false;
      this.selectTab = "editor";
      this.error_message = "";

      this.validation_errors = {
        title: false,
        thumbnail: false,
        companyInfo: [],
        policy: false,
        category: false
      };

      this.form = {
        thumbnail_file: null,
        title: "",
        content: "",
        status: "draft",
        category: "",   // ★ 単一カテゴリ
        companyInfoName: "",
        companyInfo: [
          { name: "企業名", content: "", required: true, placeholder: "例）◯◯株式会社" },
          { name: "電話番号", content: "", required: true, placeholder: "例）03-0000-0000" },
          { name: "所在地", content: "", required: true, placeholder: "例）東京都◯◯区◯◯ 1-2-3" }
        ]
      };
    },

    submitPost() {
      if (this.is_sending) return;
      if (!this.validateForm()) return;

      this.is_sending = true;
      this.send_success = false;

      const contentHtml =
        this.convertToBlocks(this.form.content) +
        this.companyInfoTable;

      const formData = new FormData();
      formData.append("thumbnail", this.form.thumbnail_file);
      formData.append("title", this.form.title);
      formData.append("content", contentHtml);
      formData.append("status", this.form.status);

      // ★ 単一カテゴリを WordPress に送信
      formData.append("categories", JSON.stringify([this.form.category]));

      axios.post("https://k-ho-ko.com/api-post.php", formData)
        .then(() => {
          this.send_success = true;
          this.resetForm();
        })
        .catch(() => {
          this.error_message = "送信に失敗しました。時間をおいて再度お試しください。";
        })
        .finally(() => {
          this.is_sending = false;
        });
    }
  },

  mounted() {
    const Clipboard = Quill.import("modules/clipboard");

    class CleanClipboard extends Clipboard {
      convert(html = null) {
        let delta = super.convert(html);
        delta.ops = delta.ops.map(op => {
          if (op.insert && typeof op.insert === "string") {
            op.insert = op.insert
              .replace(/<h[1-6][^>]*>/gi, "")
              .replace(/<\/h[1-6]>/gi, "");
          }
          return op;
        });
        return delta;
      }
    }

    Quill.register("modules/clipboard", CleanClipboard, true);

    axios
      .get("https://k-ho-ko.com/wp-json/wp/v2/categories?per_page=100&hide_empty=false&_fields=id,name")
      .then((res) => {
        // ★ ID=1（企業情報）を UI から除外
        this.categories = res.data.filter(cat => cat.id !== 1);
      })
      .catch(() => {
        this.error_message =
          "カテゴリー情報の取得に失敗しました。時間をおいて再度お試しください。";
      });
  }
};
</script>



<style>

/* ============================
   全背景を薄いグレーに統一（最強版）
   ============================ */

:root {
  --color-bg: #f5f7fa;        /* 薄いグレー */
  --color-card: #f5f7fa;      /* カードも薄いグレー */
  --color-border: #dde3ec;
  --color-primary: #0066cc;
  --color-primary-dark: #004b99;
  --color-danger: #d93025;
  --color-text: #333333;
  --color-muted: #666666;
}

/* ページ全体を薄いグレーに強制 */
html,
body,
#app,
.page-header,
.form-container,
.form-section,
.editor-wrapper,
.ql-container,
.preview-box {
  background: var(--color-bg) !important;
}

/* body */
body {
  margin: 0;
  background: var(--color-bg) !important;
}

/* app */
#app {
  max-width: 900px;
  margin: 40px auto;
  padding: 0 8px 40px; /* ← 余白を最小化して幅を広げる */
  background: var(--color-bg) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: var(--color-text);
  box-sizing: border-box;
}

/* ヘッダー */
.page-header {
  margin-bottom: 24px;
  background: var(--color-bg) !important;
}

.page-description {
  margin: 0;
  color: var(--color-muted);
  font-size: 14px;
}

/* セクション */
.form-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  background: var(--color-bg) !important;
}

.form-section {
  background: var(--color-bg) !important;
  border-radius: 8px;
  padding: 0 8px 12px; /* ← 余白最小化 */
  border: 1px solid var(--color-border);
}

/* フォーム要素 */
.form-input {
  width: 100% !important;
  max-width: 100% !important;
  padding: 14px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 17px;
  box-sizing: border-box;
  background: var(--color-bg) !important;
}

/* ラジオボタン・チェックボックス */
input[type="radio"],
input[type="checkbox"] {
  accent-color: var(--color-primary);
  background-color: var(--color-bg) !important;
}

/* エディタ */
.editor-wrapper,
.ql-container,
.ql-editor {
  width: 100% !important;
  max-width: 100% !important;
  background: var(--color-bg) !important;
}

.ql-container {
  min-height: 240px;
}

.ql-editor {
  font-size: 17px;
  padding: 14px !important;
  line-height: 1.6;
}

/* プレビュー */
.preview-box {
  background: var(--color-bg) !important;
  padding: 14px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
}

/* ============================
   カテゴリを横並びに戻す（2列）
   ============================ */
.category-list {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
  padding: 0 !important;
}

.category-item {
  width: calc(50% - 8px) !important;  /* ← 2列で広く */
  min-width: calc(50% - 8px) !important;
  background: var(--color-bg) !important;
  padding: 6px 0 !important;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
}

/* 掲載条件 */
.policy-box,
.policy-label {
  background: var(--color-bg) !important;
}

/* ボタン（青のまま） */
.btn-primary {
  background: var(--color-primary);
  color: #fff;
}

.btn-secondary {
  background: #e6eef7;
  color: var(--color-primary-dark);
}

/* スマホ最適化 */
@media (max-width: 600px) {

  #app {
    margin: 20px auto;
    padding: 0 6px 30px; /* ← さらに余白を狭く */
  }

  .form-section {
    padding: 0 6px 16px !important;
    border: none;
  }

  .form-input {
    font-size: 17px;
    padding: 14px;
  }

  .ql-editor {
    font-size: 17px;
    padding: 14px !important;
  }

  /* カテゴリ（スマホでも2列維持） */
  .category-item {
    width: calc(50% - 6px) !important;
    min-width: calc(50% - 6px) !important;
  }
}

</style>
